import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';

import { environment } from '../../../../environment';
import { UserType } from '../../../../types/user.type';

import './glassesPairing.styles.scss';

/**
 * Modal de emparejamiento de las gafas por QR.
 *
 * Las Magic Leap 2 no tienen teclado, así que en lugar de escribir credenciales
 * en el dispositivo se muestra un QR en el equipo del usuario —que ya está
 * autenticado— y las gafas lo escanean con su lector de marcadores.
 *
 * El QR no lleva ni token ni sala: solo un código de un solo uso que caduca en
 * 60 segundos. Cuando las gafas lo canjean, este modal pide confirmación al
 * usuario antes de emitir la credencial. Ese paso es deliberado: el QR se
 * muestra dentro de una aplicación donde se comparte pantalla, así que hay que
 * asumir que puede filtrarse, y la confirmación es lo que impide que quien lo
 * vea complete el emparejamiento por su cuenta.
 */

interface GlassesPairingProps {
  user: UserType;
  room: string;
  onClose: () => void;
}

type PairingStatus =
  | 'loading'
  | 'waiting'          // QR en pantalla, nadie ha escaneado
  | 'pending_confirm'  // las gafas han escaneado, falta confirmar
  | 'confirmed'
  | 'expired'
  | 'rejected'
  | 'error';

interface PairingSession {
  pairing_id: string;
  pairing_code: string;
  qr_payload: string;
  expires_at: string;
  expires_in: number;
}

/** Cada cuánto se pregunta al servidor si alguien ha escaneado */
const POLL_MS = 1500;

export function GlassesPairingComponent({ user, room, onClose }: GlassesPairingProps) {
  const { t } = useTranslation();

  const [status, setStatus] = useState<PairingStatus>('loading');
  const [session, setSession] = useState<PairingSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [device, setDevice] = useState<{ type: string; name?: string; serial?: string } | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const authHeaders = { headers: { Authorization: `Bearer ${user.access_token}` } };

  const stopTimers = useCallback(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
  }, []);

  /** Pide una sesión nueva y pinta el QR */
  const createSession = useCallback(async () => {
    stopTimers();
    setStatus('loading');
    setErrorMessage(null);
    setDevice(null);

    try {
      const { data } = await axios.post<PairingSession>(
        `${environment.api.host}/api/device-pairing`,
        { room, device_type: 'magic-leap-2', host: new URL(environment.api.host).host },
        authHeaders,
      );

      setSession(data);
      setSecondsLeft(data.expires_in);

      // Nivel de corrección alto y buen margen: el QR se lee a distancia y con
      // luz irregular, y las gafas agradecen el contraste.
      const url = await QRCode.toDataURL(data.qr_payload, {
        errorCorrectionLevel: 'H',
        margin: 2,
        width: 320,
        color: { dark: '#0E1420', light: '#FFFFFF' },
      });

      setQrDataUrl(url);
      setStatus('waiting');
    } catch (error) {
      console.error('[Pairing] No se pudo crear la sesión:', error);
      setErrorMessage(String(t('pairing.error.create')));
      setStatus('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, user.access_token]);

  useEffect(() => {
    createSession();
    return stopTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Sondeo del estado mientras la sesión está viva */
  useEffect(() => {
    if (!session || (status !== 'waiting' && status !== 'pending_confirm')) return;

    pollTimer.current = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${environment.api.host}/api/device-pairing/${session.pairing_id}`,
          authHeaders,
        );

        if (!data) return;

        if (data.status === 'pending_confirm') {
          setDevice(data.device);
          setStatus('pending_confirm');
        } else if (data.status === 'confirmed' || data.status === 'consumed') {
          setStatus('confirmed');
          stopTimers();
        } else if (data.status === 'expired') {
          setStatus('expired');
          stopTimers();
        } else if (data.status === 'rejected') {
          setStatus('rejected');
          stopTimers();
        }
      } catch (error) {
        console.warn('[Pairing] Fallo al consultar el estado:', error);
      }
    }, POLL_MS);

    return () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, status]);

  /** Cuenta atrás del código */
  useEffect(() => {
    if (status !== 'waiting') return;

    countdownTimer.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimer.current) { clearInterval(countdownTimer.current); countdownTimer.current = null; }
    };
  }, [status]);

  const confirm = async () => {
    if (!session) return;
    try {
      await axios.post(`${environment.api.host}/api/device-pairing/${session.pairing_id}/confirm`, {}, authHeaders);
      setStatus('confirmed');
      stopTimers();
    } catch (error) {
      setErrorMessage(String(t('pairing.error.confirm')));
    }
  };

  const reject = async () => {
    if (!session) return;
    try {
      await axios.post(`${environment.api.host}/api/device-pairing/${session.pairing_id}/reject`, {}, authHeaders);
    } finally {
      setStatus('rejected');
      stopTimers();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const deviceLabel = device?.name || device?.type || t('pairing.device.generic');

  return (
    <div className="GlassesPairing" role="dialog" aria-modal="true" aria-label={String(t('pairing.title'))}>
      <div className="pairing-panel">
        <button className="pairing-close" onClick={onClose} aria-label={String(t('pairing.close'))}>
          <i className="las la-times" />
        </button>

        <h2 className="pairing-title">{t('pairing.title')}</h2>

        {status === 'loading' && (
          <p className="pairing-hint">{t('pairing.loading')}</p>
        )}

        {status === 'waiting' && session && (
          <>
            <p className="pairing-hint">{t('pairing.instructions')}</p>

            <div className="pairing-qr">
              {qrDataUrl && <img src={qrDataUrl} alt={String(t('pairing.qr.alt'))} />}
            </div>

            <div className="pairing-code" aria-live="polite">
              <span className="pairing-code-label">{t('pairing.code.label')}</span>
              <span className="pairing-code-value">{session.pairing_code}</span>
            </div>

            <p className={`pairing-countdown ${secondsLeft <= 10 ? 'is-urgent' : ''}`}>
              {t('pairing.expires_in', { seconds: secondsLeft })}
            </p>
          </>
        )}

        {status === 'pending_confirm' && (
          <div className="pairing-confirm">
            <i className="las la-vr-cardboard pairing-confirm-icon" />
            <p className="pairing-confirm-question">{t('pairing.confirm.question', { device: deviceLabel })}</p>
            {device?.serial && <p className="pairing-confirm-serial">{device.serial}</p>}
            <p className="pairing-hint">{t('pairing.confirm.warning')}</p>

            <div className="pairing-actions">
              <button className="pairing-btn is-primary" onClick={confirm}>
                <i className="las la-check" /> {t('pairing.confirm.accept')}
              </button>
              <button className="pairing-btn is-ghost" onClick={reject}>
                {t('pairing.confirm.reject')}
              </button>
            </div>
          </div>
        )}

        {status === 'confirmed' && (
          <div className="pairing-result is-ok">
            <i className="las la-check-circle" />
            <p>{t('pairing.confirmed')}</p>
            <button className="pairing-btn is-primary" onClick={onClose}>{t('pairing.done')}</button>
          </div>
        )}

        {(status === 'expired' || status === 'rejected' || status === 'error') && (
          <div className="pairing-result is-warn">
            <i className="las la-exclamation-circle" />
            <p>
              {status === 'expired' && t('pairing.expired')}
              {status === 'rejected' && t('pairing.rejected')}
              {status === 'error' && (errorMessage ?? t('pairing.error.create'))}
            </p>
            <button className="pairing-btn is-primary" onClick={createSession}>
              <i className="las la-redo-alt" /> {t('pairing.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
